import {
  AttributeDataType,
  IdentifierSchema,
  type AttributeDataTypeType,
  type ResourceLookupType,
  type WorkflowType,
  type WorkflowVariableDateResolutionType,
} from "@validereinc/domain";

const structuredWorkflowVariableDataTypes = [
  AttributeDataType.GEO_POINT,
  AttributeDataType.NUMBER_ARRAY,
  AttributeDataType.FILE,
  AttributeDataType.MULTI_FILE,
  AttributeDataType.DATE_TIME_RANGE,
  AttributeDataType.MULTI_PICK_LIST,
  AttributeDataType.MULTI_LOOKUP,
  AttributeDataType.LOOKUP,
] as const satisfies readonly AttributeDataTypeType[];

export type WorkflowDetailDisplayVariableType = {
  variableName: string;
  displayName: string;
  dataType: AttributeDataTypeType;
  lookupEntityType?: ResourceLookupType;
  dateResolution?: WorkflowVariableDateResolutionType;
  value: unknown;
};

const parseStructuredWorkflowVariableText = (value: string): unknown => {
  const trimmedValue = value.trim();
  if (!trimmedValue.startsWith("{") && !trimmedValue.startsWith("[")) {
    return value;
  }

  try {
    return JSON.parse(trimmedValue);
  } catch {
    return value;
  }
};

/**
 * Turns a stored workflow variable into the shape `displayValueByDataType` expects.
 * Structured values are sometimes persisted as JSON text.
 */
export const coerceWorkflowVariableValueForDisplay = (
  value: unknown,
  dataType: AttributeDataTypeType
): unknown => {
  if (value == null || typeof value !== "string") {
    return value;
  }

  if (dataType === AttributeDataType.BOOLEAN) {
    if (value === "true") return true;
    if (value === "false") return false;
    return value;
  }

  if (
    dataType === AttributeDataType.NUMBER ||
    dataType === AttributeDataType.INTEGER
  ) {
    if (value.trim() === "" || Number.isNaN(Number(value))) {
      return value;
    }

    return dataType === AttributeDataType.INTEGER
      ? Number.parseInt(value, 10)
      : Number(value);
  }

  if (
    (
      structuredWorkflowVariableDataTypes as readonly AttributeDataTypeType[]
    ).includes(dataType)
  ) {
    return parseStructuredWorkflowVariableText(value);
  }

  return value;
};

/**
 * Maps variable names through metadata, in the given order.
 * Names missing from `variable_metadata` are skipped.
 */
export const getWorkflowDisplayVariables = (
  workflow: Pick<WorkflowType, "config" | "variables"> | undefined,
  variableNames: readonly string[] | undefined
): WorkflowDetailDisplayVariableType[] => {
  if (!workflow || !variableNames?.length) {
    return [];
  }

  const variableMetadata = workflow.config.variable_metadata ?? {};

  return variableNames.flatMap((variableName) => {
    const metadata = variableMetadata[variableName];
    if (!metadata) {
      return [];
    }

    return [
      {
        variableName,
        displayName: metadata.display_name,
        dataType: metadata.data_type,
        lookupEntityType: metadata.lookup_entity_type,
        dateResolution: metadata.date_resolution,
        value: coerceWorkflowVariableValueForDisplay(
          workflow.variables?.[variableName],
          metadata.data_type
        ),
      },
    ];
  });
};

/** Workflow variables configured for the workflow page, in display order. */
export const getWorkflowDetailDisplayVariables = (
  workflow: Pick<WorkflowType, "config" | "variables"> | undefined
): WorkflowDetailDisplayVariableType[] =>
  getWorkflowDisplayVariables(workflow, workflow?.config.variables_to_display);

/** Lookup ids from display variables, grouped for `useMultiLookupAssetDetails`. */
export const getWorkflowLookupIdsByEntityType = (
  displayVariables: WorkflowDetailDisplayVariableType[]
): Map<ResourceLookupType, Set<string>> => {
  const idsByEntityType = new Map<ResourceLookupType, Set<string>>();

  displayVariables.forEach((variable) => {
    if (
      variable.dataType !== AttributeDataType.LOOKUP ||
      !variable.lookupEntityType
    ) {
      return;
    }

    const parsedLookupId = IdentifierSchema.safeParse(variable.value);
    if (!parsedLookupId.success) {
      return;
    }

    const idsForEntityType =
      idsByEntityType.get(variable.lookupEntityType) ?? new Set<string>();
    idsForEntityType.add(parsedLookupId.data);
    idsByEntityType.set(variable.lookupEntityType, idsForEntityType);
  });

  return idsByEntityType;
};
